/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description React Hook useEligibilityData
 */
import _ from 'lodash';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import api from 'client/index';
import { JsonRule, JsonTree } from '@react-awesome-query-builder/antd';
import { parseQueryFilterParams } from 'components/forms/schema/claimsAdvancedFilterSchema';
import { OPERATORS } from 'lib/SearchQueryBuilder/config/queryConfig';
import { mapClaimStatusLabelToCodes } from 'utils/claims/claimStatusRequests';
import { addRuleToQuery, prepareQueryRule } from 'utils/queryBuilder';
import QUERY_KEYS from 'app-constants/queryKeys';
import { GROUPED_CLAIM_WORK_STATES } from 'app-constants/claims/claimRequest';
import { POST_PAYMENT_GROUPED_STATUS } from 'app-constants/claims/era';
import { DEFAULT_PAGINATION } from '@veritable/common/constants';
import { getClaimAdfFieldMap, filterDataKeys } from 'utils/adfUtils';
import { buildCriteria } from 'utils/advancedFilterUtils';
import { useCustomAttributesStoreValues } from 'store/customAttributes';
import { CUSTOM_ATTRIBUTE_ENTITY_TYPE } from 'app-constants/admin/customAttribute';

/**
 * use eligibility data hook
 * @returns {UseQueryResult} useEligibilityData
 */
interface IProps {
	apiParams: ClaimStatusRequests.IClaimSearch;
	pageNumber?: number;
	pageSize?: number;
	workStateGroup?: ClaimRequest.IGroupedClaimWorkState;
	batchPostPaymentMeta?: {
		batchId: string;
		status: PatientPayment.IPostPaymentGroupedStatus;
	};
}

// function to get worklist rule
const getWorklistRule = (workStateGroup: ClaimRequest.IGroupedClaimWorkState): JsonRule => {
	const workState = GROUPED_CLAIM_WORK_STATES[workStateGroup]?.value || [];
	return prepareQueryRule({
		field: filterDataKeys.workState,
		operator: OPERATORS.multiselectContains,
		value: [workState],
	});
};

// append work-state-group and batch-post-payment scope rules so reads & exports filter identically
const addClaimRequestScopeRules = (
	criteria: JsonTree | null,
	{ workStateGroup, batchPostPaymentMeta }: Pick<IProps, 'workStateGroup' | 'batchPostPaymentMeta'>,
): JsonTree | null => {
	let result = criteria;
	// insert custom criteria for work state group
	if (workStateGroup) {
		result = addRuleToQuery(result, getWorklistRule(workStateGroup));
	}
	// insert custom criteria for batch post payment
	if (!_.isEmpty(batchPostPaymentMeta)) {
		const batchPostPaymentIdRule = prepareQueryRule({
			field: filterDataKeys.batchPostPaymentId,
			operator: OPERATORS.equal,
			value: [batchPostPaymentMeta.batchId],
		});

		// status to pass to api
		const groupedStatus = POST_PAYMENT_GROUPED_STATUS?.[batchPostPaymentMeta.status]?.groupedValue;
		const batchPostPaymentStatusRule = prepareQueryRule({
			field: filterDataKeys.batchPostPaymentStatus,
			operator: OPERATORS.multiselectContains,
			value: [groupedStatus],
		});
		result = addRuleToQuery(result, batchPostPaymentIdRule);
		result = addRuleToQuery(result, batchPostPaymentStatusRule);
	}
	return result;
};

const useAllClaimStatusRequests = (
	{
		apiParams,
		pageNumber = DEFAULT_PAGINATION.pageNumber,
		pageSize = DEFAULT_PAGINATION.pageSize,
		workStateGroup,
		batchPostPaymentMeta,
	}: IProps,
	options?: IQueryOptions,
): UseQueryResult<ClaimStatusRequests.IModel> => {
	const { customAttributes } = useCustomAttributesStoreValues();
	const claimAttributes = customAttributes?.[CUSTOM_ATTRIBUTE_ENTITY_TYPE.claim];
	return useQuery({
		queryKey: [QUERY_KEYS.claimStatusRequests, apiParams, pageNumber, pageSize, workStateGroup, batchPostPaymentMeta, claimAttributes],
		queryFn: async () => {
			let criteria = parseQueryFilterParams({
				query: _.cloneDeep(apiParams?.criteria) as JsonTree,
				field: filterDataKeys.claimStatus,
				parser: mapClaimStatusLabelToCodes,
			});
			criteria = addClaimRequestScopeRules(criteria, { workStateGroup, batchPostPaymentMeta });
			const { data } = await api.FHIRClaimApi.getClaims({
				claimSearch: {
					...apiParams,
					...(buildCriteria(criteria, getClaimAdfFieldMap(claimAttributes)) || {}),
				},
				pgNum: pageNumber,
				pgSize: pageSize,
			});
			return data;
		},
		...options,
	});
};

export { getWorklistRule, addClaimRequestScopeRules };
export default useAllClaimStatusRequests;
